/**
 * Custom Measurements JS with event debouncing
 * This will prevent multiple events from firing for a single click
 * Updated to work with custom form fields
 */

document.addEventListener("DOMContentLoaded", function () {
  // Define CustomMeasurements object
  const CustomMeasurements = {
    // Current unit
    currentUnit: "cm",

    // Flag to track initialization
    initialized: false,

    // Conversion timestamp to prevent multiple conversions in a short period
    lastConversionTime: 0,

    // Store elements once found
    elements: {},

    /**
     * Main initialization function
     */
    init: function () {
      // Check if already initialized to prevent double initialization
      if (this.initialized) {
        console.log("CustomMeasurements: Already initialized, skipping");
        return;
      }

      console.log("CustomMeasurements: Initializing...");
      this.initialized = true;

      // Find all required elements
      this.refreshElements();

      // Set up all functionality (only if we found our container)
      if (this.elements.container) {
        this.storeInitialValues(); // Store original values first
        this.setupUnitToggle();
        this.setupNumberValidation();
        this.setupMeasuringGuideModal();
        this.setupOptionGroups();
        this.setupResetButton();
        this.setupVerificationCheckbox();
        console.log("CustomMeasurements: Initialization complete");
      } else {
        console.log(
          "CustomMeasurements: Container not found, initialization skipped"
        );
      }
    },

    /**
     * Find all required elements in the DOM
     */
    refreshElements: function () {
      console.log("CustomMeasurements: Finding elements...");

      // Get the main container first
      const container = document.querySelector(".cm-container");

      // Only find other elements if container exists
      if (container) {
        this.elements = {
          container: container,
          optionGroups: container.querySelectorAll(".cm-option-group"),
          unitButtons: container.querySelectorAll(".cm-unit-btn"),
          resetButton: container.querySelector(".cm-reset-btn"),
          // Updated selectors for custom form fields
          measurementInputs: container.querySelectorAll(".cm-custom-input"),
          unitLabels: container.querySelectorAll(".field__unit-text"),
          modalButton: container.querySelector(
            "#cm-modal-button, .cm-guide-btn"
          ),
          selectedOptionField: document.getElementById("cm-selected-option"),
          oldModal: document.getElementById("measuring-modal"),
          newModal: document.getElementById("measuring-modal-v2"),
        };
      } else {
        this.elements = {};
      }

      // Log what we found
      console.log("CustomMeasurements: Elements found:", {
        Container: !!this.elements.container,
        "Modal Button": !!this.elements.modalButton,
        "Reset Button": !!this.elements.resetButton,
        "Unit Buttons": this.elements.unitButtons?.length || 0,
        "Option Groups": this.elements.optionGroups?.length || 0,
        "Measurement Inputs": this.elements.measurementInputs?.length || 0,
        "Unit Labels": this.elements.unitLabels?.length || 0,
        "Old Modal Present": !!this.elements.oldModal,
        "New Modal Present": !!this.elements.newModal,
      });
    },

    /**
     * Store initial values from inputs for conversion later
     */
    storeInitialValues: function () {
      const { measurementInputs } = this.elements;

      if (!measurementInputs || measurementInputs.length === 0) return;

      console.log("CustomMeasurements: Storing initial values");

      measurementInputs.forEach((input) => {
        // Only store if no original values are saved yet
        if (
          !input.hasAttribute("data-original-value") ||
          !input.hasAttribute("data-original-unit")
        ) {
          // Store current value (even if empty)
          input.setAttribute("data-original-value", input.value || "");
          input.setAttribute("data-original-unit", this.currentUnit);

          // Debug log
          if (input.value && input.value.trim() !== "") {
            console.log(
              `Stored initial value: ${input.value} ${this.currentUnit}`
            );
          }
        }
      });
    },

    /**
     * DIRECT CONVERSION FUNCTIONS
     */
    cmToMm: function (value) {
      return Number(value) * 10;
    },

    cmToIn: function (value) {
      return Number(value) * 0.3937008;
    },

    mmToCm: function (value) {
      return Number(value) * 0.1;
    },

    mmToIn: function (value) {
      return Number(value) * 0.0393701;
    },

    inToCm: function (value) {
      return Number(value) * 2.54;
    },

    inToMm: function (value) {
      return Number(value) * 25.4;
    },

    /**
     * Convert a value from one unit to another
     */
    convertValue: function (value, fromUnit, toUnit) {
      // Make sure we have valid input
      if (value === null || value === undefined || value === "") {
        return "";
      }

      // Ensure we're working with a number
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return value; // Return original if not a number
      }

      // No conversion needed if units are the same
      if (fromUnit === toUnit) {
        return numValue;
      }

      // Direct conversions
      if (fromUnit === "cm" && toUnit === "mm") {
        return this.cmToMm(numValue);
      } else if (fromUnit === "cm" && toUnit === "in") {
        return this.cmToIn(numValue);
      } else if (fromUnit === "mm" && toUnit === "cm") {
        return this.mmToCm(numValue);
      } else if (fromUnit === "mm" && toUnit === "in") {
        return this.mmToIn(numValue);
      } else if (fromUnit === "in" && toUnit === "cm") {
        return this.inToCm(numValue);
      } else if (fromUnit === "in" && toUnit === "mm") {
        return this.inToMm(numValue);
      }

      // Fallback
      return numValue;
    },

    /**
     * Format a measurement value based on unit
     */
    formatValue: function (value, unit) {
      // Make sure we have valid input
      if (value === null || value === undefined || value === "") {
        return "";
      }

      // Ensure we're working with a number
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return value; // Return original if not a number
      }

      // For whole numbers, don't add decimal places
      if (Math.floor(numValue) === numValue) {
        return numValue.toString();
      }

      // For values with decimals
      if (unit === "in") {
        // Always use 2 decimal places for inches
        return numValue.toFixed(2);
      } else if (numValue < 1) {
        // Use 2 decimal places for small cm/mm values
        return numValue.toFixed(2);
      } else {
        // Use 1 decimal place for normal cm/mm values
        return numValue.toFixed(1);
      }
    },

    /**
     * Set up unit toggle buttons (cm/mm/in)
     * With debounce to prevent double-firing
     */
    setupUnitToggle: function () {
      const { unitButtons } = this.elements;

      if (!unitButtons || unitButtons.length === 0) {
        console.warn("CustomMeasurements: No unit buttons found");
        return;
      }

      console.log(
        "CustomMeasurements: Setting up unit toggle with",
        unitButtons.length,
        "buttons"
      );

      // First remove any existing click handlers to avoid duplicates
      unitButtons.forEach((button) => {
        // Clone the button to remove all event listeners
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
      });

      // Re-query the buttons after replacing them
      this.elements.unitButtons =
        this.elements.container.querySelectorAll(".cm-unit-btn");
      const freshButtons = this.elements.unitButtons;

      // Store a reference to this for closure access
      const self = this;

      // Set up each unit button
      freshButtons.forEach((button) => {
        // Get unit type from data attribute or text content
        const unitType =
          button.getAttribute("data-unit") ||
          button.textContent.trim().toLowerCase();

        // Skip invalid unit types
        if (!["cm", "mm", "in"].includes(unitType)) {
          console.warn("CustomMeasurements: Invalid unit type:", unitType);
          return;
        }

        // Add missing data-unit attribute if needed
        if (!button.getAttribute("data-unit")) {
          button.setAttribute("data-unit", unitType);
        }

        // Add click event
        button.addEventListener("click", function (event) {
          // Prevent default behavior
          event.preventDefault();

          console.log(`CustomMeasurements: Unit button clicked: ${unitType}`);

          // Skip if already active
          if (self.currentUnit === unitType) {
            console.log(
              `CustomMeasurements: ${unitType} already active, skipping conversion`
            );
            return;
          }

          // IMPORTANT: Debounce conversions - prevent multiple conversions within 300ms
          const now = Date.now();
          if (now - self.lastConversionTime < 300) {
            console.log(
              "CustomMeasurements: Too soon after previous conversion, skipping"
            );
            return;
          }
          self.lastConversionTime = now;

          // Get old unit for conversion
          const oldUnit = self.currentUnit;
          console.log(
            `CustomMeasurements: Converting from ${oldUnit} to ${unitType}`
          );

          // Update current unit
          self.currentUnit = unitType;

          // Update all buttons - remove active class from ALL buttons
          freshButtons.forEach((btn) => {
            btn.classList.remove("active");
          });

          // Add active class to THIS button
          this.classList.add("active");

          // Update unit labels
          if (self.elements.unitLabels) {
            self.elements.unitLabels.forEach((label) => {
              label.textContent = unitType;
            });
          }

          // Convert all measurement input values
          if (self.elements.measurementInputs) {
            self.elements.measurementInputs.forEach((input) => {
              const currentValue = input.value;

              if (currentValue && currentValue.trim() !== "") {
                // CM to MM special case for highest accuracy
                if (oldUnit === "cm" && unitType === "mm") {
                  const numValue = Number(currentValue);
                  console.log(
                    "DEBUG cmToMm: Input value =",
                    currentValue,
                    "type =",
                    typeof currentValue
                  );
                  if (!isNaN(numValue)) {
                    const result = numValue * 10;
                    console.log(
                      "DEBUG cmToMm: Result =",
                      result,
                      "(",
                      numValue,
                      "* 10)"
                    );
                    input.value =
                      Math.floor(result) === result
                        ? result.toString()
                        : result.toFixed(1);
                    console.log(
                      "DEBUG cmToMm: Final value set to",
                      input.value
                    );
                  }
                } else {
                  // First check if we have original values to use as source of truth
                  const originalValue = input.getAttribute(
                    "data-original-value"
                  );
                  const originalUnit = input.getAttribute("data-original-unit");

                  if (originalValue && originalUnit) {
                    // Convert directly from original value to new unit
                    const numOriginal = Number(originalValue);
                    if (!isNaN(numOriginal)) {
                      const convertedValue = self.convertValue(
                        numOriginal,
                        originalUnit,
                        unitType
                      );
                      input.value = self.formatValue(convertedValue, unitType);
                    }
                  } else {
                    // Fallback to direct conversion from old unit if no original value
                    const numValue = Number(currentValue);
                    if (!isNaN(numValue)) {
                      const convertedValue = self.convertValue(
                        numValue,
                        oldUnit,
                        unitType
                      );
                      input.value = self.formatValue(convertedValue, unitType);
                    }
                  }
                }
              }

              // Update input field names
              if (input.name && input.name.includes("properties[")) {
                // Extract the full property name without the unit part
                const propertyName = input.name
                  .split("[")[1]
                  .split("(")[0]
                  .trim();
                input.name = `properties[${propertyName} (${unitType})]`;
              }
            });
          }

          console.log(`CustomMeasurements: Conversion to ${unitType} complete`);
        });

        console.log(
          "CustomMeasurements: Added click handler to unit button:",
          unitType
        );
      });
    },

    /**
     * Setup reset button functionality
     */
    setupResetButton: function () {
      const { resetButton } = this.elements;

      if (!resetButton) {
        console.warn("CustomMeasurements: Reset button not found");
        return;
      }

      console.log("CustomMeasurements: Setting up reset button");

      // Store a reference to this for closure access
      const self = this;

      // First remove any existing click handlers
      const newResetButton = resetButton.cloneNode(true);
      resetButton.parentNode.replaceChild(newResetButton, resetButton);

      // Update the reference
      this.elements.resetButton = newResetButton;

      // Add click event to reset button
      newResetButton.addEventListener("click", function (event) {
        event.preventDefault();
        console.log("CustomMeasurements: Reset button clicked");
        self.resetMeasurements();
      });

      console.log("CustomMeasurements: Reset button setup complete");
    },

    /**
     * Reset all measurement inputs to their original values
     */
    resetMeasurements: function () {
      console.log(
        "CustomMeasurements: Resetting all measurements to original values"
      );

      const { measurementInputs } = this.elements;

      if (!measurementInputs || measurementInputs.length === 0) {
        console.warn("CustomMeasurements: No measurement inputs to reset");
        return;
      }

      // Clear all input fields
      measurementInputs.forEach((input) => {
        // Clear the input value
        console.log("CustomMeasurements: Clearing input", input);
        input.value = "";
      });

      console.log("CustomMeasurements: Measurements reset complete");
    },

    /**
     * Set up number input validation for measurement fields
     */
    setupNumberValidation: function () {
      const { measurementInputs } = this.elements;

      if (!measurementInputs || measurementInputs.length === 0) {
        console.warn("CustomMeasurements: No measurement inputs found");
        return;
      }

      console.log(
        "CustomMeasurements: Setting up validation for",
        measurementInputs.length,
        "inputs"
      );

      // Store a reference to this for closure access
      const self = this;

      measurementInputs.forEach((input) => {
        // Remove existing listeners by cloning
        const newInput = input.cloneNode(true);
        input.parentNode.replaceChild(newInput, input);

        // Live validation during input
        newInput.addEventListener("input", function () {
          // Store current cursor position
          const cursorPos = this.selectionStart;

          // Remove non-numeric characters except decimal point
          let value = this.value.replace(/[^\d.]/g, "");

          // Ensure only one decimal point
          const decimalPoints = value.match(/\./g);
          if (decimalPoints && decimalPoints.length > 1) {
            value = value.replace(/\.(?=.*\.)/g, "");
          }

          // Update with cleaned value
          this.value = value;

          // Restore cursor position
          if (cursorPos && cursorPos <= value.length) {
            this.setSelectionRange(cursorPos, cursorPos);
          }
        });

        // Format on blur
        newInput.addEventListener("blur", function () {
          if (this.value === "") return;
          this.value = self.formatValue(this.value, self.currentUnit);
        });
      });

      // Update measurement inputs reference after cloning
      this.elements.measurementInputs =
        this.elements.container.querySelectorAll(".cm-custom-input");
    },

    /**
     * Set up measuring guide modal
     */
    setupMeasuringGuideModal: function () {
      const { modalButton, oldModal, newModal } = this.elements;

      if (!modalButton) {
        console.warn("CustomMeasurements: Modal button not found");
        return;
      }

      console.log(
        "CustomMeasurements: Setting up measuring guide modal button"
      );

      // Remove existing listeners
      const newModalButton = modalButton.cloneNode(true);
      modalButton.parentNode.replaceChild(newModalButton, modalButton);

      // Update reference
      this.elements.modalButton = newModalButton;

      newModalButton.addEventListener("click", function (event) {
        event.preventDefault();
        console.log("CustomMeasurements: Measuring guide button clicked");

        // First try to open the new enhanced modal
        if (newModal) {
          console.log("Opening enhanced modal v2");
          document.dispatchEvent(new CustomEvent("open-measurement-modal-v2"));
        }
        // Fallback to the old modal if new one is not available
        else if (oldModal) {
          console.log("Fallback to original modal");
          document.dispatchEvent(new CustomEvent("open-measurement-modal"));
        } else {
          console.warn("No modal found on page");
        }
      });

      console.log("CustomMeasurements: Modal button setup complete");
    },

    /**
     * setupOptionGroups - Disable automatic scrolling on desktop while ensuring boxes open correctly
     */
    setupOptionGroups: function () {
      const { optionGroups, selectedOptionField } = this.elements;

      if (!optionGroups || optionGroups.length === 0) {
        console.warn("CustomMeasurements: No option groups found");
        return;
      }

      console.log(
        "CustomMeasurements: Setting up",
        optionGroups.length,
        "option groups"
      );

      // Store a reference to this for use in event handlers
      const self = this;

      // Define the isDesktop function to check each time
      function checkIsDesktop() {
        return window.innerWidth >= 1024;
      }

      optionGroups.forEach((details) => {
        // Set up toggle event
        details.addEventListener("toggle", function () {
          if (this.open) {
            console.log(
              "CustomMeasurements: Option group opened:",
              this.getAttribute("data-cm-option")
            );

            // Store selected option value
            const optionValue = this.getAttribute("data-cm-option");
            if (selectedOptionField && optionValue) {
              // For option 1, set the field to an empty string
              if (optionValue === "provide_now") {
                selectedOptionField.value = "";
                selectedOptionField.setAttribute("disabled", "disabled");
              } else {
                // For other options, set appropriate display value
                let displayValue = "";
                switch (optionValue) {
                  case "email_later":
                    displayValue = "Measurements Later";
                    break;
                  case "measuring_kit":
                    displayValue = "Measuring Kit";
                    break;
                }
                selectedOptionField.value = displayValue;
                selectedOptionField.removeAttribute("disabled"); // Ensure the field is enabled for other options
              }
            }

            // Make inputs required when measurements panel is open
            if (
              this.contains(document.querySelector(".cm-measurements-panel"))
            ) {
              // Updated to target custom inputs
              const inputs = this.querySelectorAll(".cm-custom-input");
              inputs.forEach((input) => (input.required = true));
            }

          } else {
            // Make inputs not required when panel is closed
            if (
              this.contains(document.querySelector(".cm-measurements-panel"))
            ) {
              // Updated to target custom inputs
              const inputs = this.querySelectorAll(".cm-custom-input");
              inputs.forEach((input) => (input.required = false));
            }
          }
        });

        // Set up summary click event - prevent scroll jump
        const summary = details.querySelector("summary");
        if (summary) {
          summary.addEventListener("click", function (event) {
            event.preventDefault();
            var scrollPos = window.scrollY;

            // Close other open details first (accordion)
            optionGroups.forEach(function(other) {
              if (other !== details && other.open) {
                other.open = false;
              }
            });

            // Toggle this one
            details.open = !details.open;

            // Restore scroll position to prevent jump
            window.scrollTo(0, scrollPos);

            setTimeout(function() {
              document.body.style.pointerEvents = "auto";
            }, 100);
          });
        }
      });
    },

    /**
     * Set up verification checkbox functionality
     */
    setupVerificationCheckbox: function () {
      const verificationCheckbox = document.getElementById("cm-verification-check");
      const addCartInstruction = document.querySelector(".cm-add-cart-instruction");

      if (!verificationCheckbox || !addCartInstruction) {
        console.warn("CustomMeasurements: Verification elements not found");
        return;
      }

      console.log("CustomMeasurements: Setting up verification checkbox");

      // Check initial state and show/hide instruction accordingly
      if (verificationCheckbox.checked) {
        addCartInstruction.style.display = "block";
        addCartInstruction.style.opacity = "1";
      } else {
        addCartInstruction.style.display = "none";
      }

      // Check if already initialized
      if (!verificationCheckbox.hasAttribute("data-initialized")) {
        verificationCheckbox.setAttribute("data-initialized", "true");

        // Add change event listener
        verificationCheckbox.addEventListener("change", function () {
          console.log(`CustomMeasurements: Verification checkbox ${this.checked ? "checked" : "unchecked"}`);

          if (this.checked) {
            addCartInstruction.style.display = "block";
            addCartInstruction.style.opacity = "0";
            setTimeout(() => {
              addCartInstruction.style.transition = "opacity 0.3s ease";
              addCartInstruction.style.opacity = "1";
            }, 10);
          } else {
            addCartInstruction.style.transition = "opacity 0.3s ease";
            addCartInstruction.style.opacity = "0";
            setTimeout(() => {
              addCartInstruction.style.display = "none";
            }, 300);
          }
        });

        console.log("CustomMeasurements: Verification checkbox setup complete");
      }
    }
  };

  // Initialize only once
  console.log("CustomMeasurements: Starting initialization");
  CustomMeasurements.init();
});